import { useState, useEffect } from "react";
import { useLang } from "../i18n/LanguageContext";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { PHONE_TEL, buildWhatsAppUrl } from "../lib/contact";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aaron-hvac/artifacts/fposwu68_6822fdb5-7f79-435c-9861-ad6a47053a6e.jpeg";

export const Header = () => {
  const { lang, toggleLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { href: "#home", label: t.nav.home, id: "nav-home" },
    { href: "#services", label: t.nav.services, id: "nav-services" },
    { href: "#advantage", label: t.nav.about, id: "nav-about" },
    { href: "#areas", label: t.nav.areas, id: "nav-areas" },
    { href: "#contact", label: t.nav.contact, id: "nav-contact" },
  ];

  const handleAnchor = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled ? "glass" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between gap-4">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => handleAnchor(e, "#home")}
          className="flex items-center gap-3 group"
          data-testid="header-logo-link"
        >
          <img
            src={LOGO_URL}
            alt="Aarón Heating & Cooling"
            className="h-12 w-12 rounded-full object-cover ring-1 ring-white/10 group-hover:ring-heat/40 transition"
            data-testid="header-logo"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-heading font-extrabold text-white text-lg tracking-tight">Aarón</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/60">Heating &amp; Cooling</span>
          </div>
        </a>

        {/* Center nav (desktop) */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {navItems.map((item) => (
            <a
              key={item.id}
              href={item.href}
              onClick={(e) => handleAnchor(e, item.href)}
              data-testid={item.id}
              className="px-4 py-2 rounded-full text-sm font-medium text-white/80 hover:text-white hover:bg-white/5 transition"
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2">
          {/* Language toggle */}
          <button
            onClick={toggleLang}
            data-testid="language-toggle"
            aria-label="Toggle language"
            className="relative h-10 w-[88px] rounded-full bg-ink-card border border-white/10 text-xs font-bold uppercase tracking-widest overflow-hidden"
          >
            <span
              className={`absolute top-1 bottom-1 w-[40px] rounded-full transition-all duration-300 ${lang === "en" ? "left-1 bg-cool" : "left-[46px] bg-heat"}`}
            />
            <span className={`absolute inset-y-0 left-0 w-[44px] flex items-center justify-center transition ${lang === "en" ? "text-white" : "text-white/60"}`}>EN</span>
            <span className={`absolute inset-y-0 right-0 w-[44px] flex items-center justify-center transition ${lang === "es" ? "text-white" : "text-white/60"}`}>ES</span>
          </button>

          {/* WhatsApp CTA */}
          <a
            href={buildWhatsAppUrl(t.whatsappMessage)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-btn"
            aria-label="WhatsApp"
            className="hidden md:inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#25D366] hover:brightness-110 transition shadow-[0_6px_18px_rgba(37,211,102,0.35)]"
          >
            <MessageCircle size={18} className="text-white" />
          </a>

          {/* Emergency call CTA */}
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="emergency-call-btn"
            className="hidden md:inline-flex items-center gap-2 h-10 px-4 rounded-full btn-heat font-semibold text-sm animate-pulse-glow"
          >
            <Phone size={16} />
            <span>{t.nav.emergency}</span>
          </a>

          {/* Mobile burger */}
          <button
            className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full bg-ink-card border border-white/10"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden glass border-t border-white/10" data-testid="mobile-nav">
          <nav className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={item.href}
                onClick={(e) => handleAnchor(e, item.href)}
                className="px-4 py-3 rounded-xl text-base text-white/85 hover:bg-white/5"
                data-testid={`mobile-${item.id}`}
              >
                {item.label}
              </a>
            ))}
            <a
              href={`tel:${PHONE_TEL}`}
              className="mt-2 inline-flex items-center justify-center gap-2 h-12 rounded-full btn-heat font-semibold"
              data-testid="mobile-emergency-call"
            >
              <Phone size={16} /> {t.nav.emergency}
            </a>
            <a
              href={buildWhatsAppUrl(t.whatsappMessage)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#25D366] text-white font-semibold"
              data-testid="mobile-whatsapp"
            >
              <MessageCircle size={16} /> WhatsApp
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
